/**
 * /dashboard — the parent home: the companion.
 *
 * Revision of 2026-09-18 (DECISIONS "The parent home is the companion",
 * guideline §2.9). Victor, with the two homes side by side: "refactor
 * /dashboard to mimic the /trfer page — Maya front and center, using the
 * beautiful subtraction for clarity process. The calendar, appointments, your
 * grants removed." The staff home's four elements (§2.6), on the family's side:
 *
 *   1. PageHead — eyebrow "Today" + the serif greeting as the H1, no lede in
 *      any mode (§2.1). Memorial: eyebrow "In memory", the child's name, the
 *      one reviewed lede (`getMemorialHero`, DRAFT PENDING TRF REVIEW).
 *   2. The onboarding banner — the one transient block above the composer
 *      while onboarding is incomplete (the parent's crisis-strip slot, Q7).
 *   3. The hero — the conversation itself (#2266, Victor 2026-10-06:
 *      "jumping to a new page is not acceptable"). `CompanionThread` renders
 *      the thread in place above `ComposerHero` (placeholder "Ask {companion}
 *      anything…", the voice button); a send, an idea, a noticing CTA all go
 *      into it with no route change, and an empty thread renders nothing, so
 *      a quiet visit keeps the blank slate. Under the composer, **Ideas for
 *      you** — the three suggestions the platform already generates for the
 *      family, the static chips until they land (`buildParentIdeas`), hidden
 *      while typing, never in memorial. On the composer, the history icon
 *      toggles the companion's past threads as a right drawer (2026-09-21,
 *      drawer since #2266). It is offered in MEMORIAL too: the composer is
 *      explicitly kept reachable there (#644), and the history is part of it.
 *      A prompt handed over from another page (`navigateToStevens`) arrives
 *      in router state and is sent once, on arrival.
 *   4. {companion} · noticed something — `buildParentPatterns` reads what the
 *      page fetches and says the waiting facts one at a time: an appointment
 *      within two days, overdue to-dos, receipts that need the family (#2973),
 *      TRF's unanswered QUICK polls, and the
 *      renewal when it is due or a draft is under way; last, the news that
 *      TRF paid, from the payment staff recorded in the last 7 days (#2972).
 *      In memorial only the polls lane speaks. #2758: while the renewal is in play its sentence
 *      leaves the pager and leads the page in its own callout, above the
 *      banner and the composer (Mario, 2026-10-12).
 *   5. For you to answer — the check-ins and form sends the family owes TRF,
 *      one to-do row each, sitting with the noticing's to-dos (Victor,
 *      2026-10-23: "Check-ins move to Today"; `(?) checkin-custom-on-today`).
 *      Each row: the title, "Due <day>" from `end_at` in the reader's zone,
 *      and Answer, which opens the Community feed's own answer flow
 *      (`PollCard`) in a dialog. A row leaves once the feed says `voted`.
 *      Quick polls stay on Community and are all the polls lane counts.
 *      Rows, not a banner: the page keeps one transient block (item 2). Shown
 *      in memorial too — like the polls lane, a send TRF addressed to the
 *      family is not treatment copy.
 *
 * What left this page on 2026-09-18, and where it went:
 *   - the Today card (cycle day, today's doses, today's rows) → Health,
 *     Calendar and Tasks already hold each of those, once (§3.5);
 *   - "A note from {companion}" (the guidance card) → lane 1 of the noticing;
 *   - the appointments list → Calendar;
 *   - Your grants (usage bars, latest receipt, the renewal row) → Finances ›
 *     Grants keeps `GrantsCard`; the renewal invitation is lane 4;
 *   - the unanswered-polls banner and its dialog → lane 3, answered on Community
 *     (quick polls; check-ins came back as item 5's rows on 2026-10-23);
 *   - the companion rail, its two doors, the printed number and "All
 *     conversations" → the composer IS the page. The sidebar row that
 *     replaced them left in turn on 2026-09-21: the archive is a section of
 *     Settings › Companion, and the quick reach is the composer's history
 *     icon;
 *   - minimise / hide / restore and the rail's drag handle → nothing is left to
 *     hide (`home-card-prefs`, `CardControls`, `RAIL_WIDTH` deleted).
 *
 * The memorial register (#644, #693) is kept and re-proven by
 * `DashboardPage.lifecycle.test.tsx`: while the family record is in flight the
 * page is treated as memorial, because the fail-safe mode is 'active' and
 * treatment copy must not flash before the record lands. The companion itself
 * stays reachable in memorial — #644's own words — so the composer renders;
 * the ideas and the treatment lanes do not.
 */
import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { OnboardingWizard } from '@/components/onboarding/OnboardingWizard';
import { useOnboardingGuide } from '@/contexts/OnboardingGuideContext';
import { OnboardingBanner } from '@/components/onboarding/OnboardingBanner';
import { FirstRunCard } from '@/components/onboarding/FirstRunCard';
import { isNoFamilyError } from '@/lib/api-errors';
import { AlertCircle } from 'lucide-react';
import { api } from '@/services/api';
import { getTimeOfDay } from '@/lib/utils';
import { readStevensPrefill } from '@/lib/chat-navigation';
import { PageHead, Emphasis } from '@/components/editorial';
import {
  resolveDashboardMode,
  getDashboardHero,
  memorialHeroCopy,
  GREETING_ALONE_KEY,
  GREETING_NAMED_KEY,
} from '@/lib/dashboard-mode';
import { NAV_KEY } from '@/i18n/keys/nav';
import { useMsg } from '@/i18n/msg';
import type { CompanionPattern } from '@/components/companion/CompanionCallout';
import { visibleParentTasks } from '@/lib/task-display';
import { useCompanionName } from '@/hooks/useCompanionName';
import { buildParentPatterns } from '@/lib/parent-home-noticing';
import { signatureRenewalDue } from '@/lib/grants-status';
import { needsAttention } from '@/lib/bills-display';
import { CLAIMS_QUERY_KEY } from '@/components/expenses/ExpenseLedger';
import { deriveRenewalCta, renewalCtaFromError, renewalYearMsgFromApp } from '@/lib/renewal-display';
import { askedItemNames, useFlaggedFieldLabels } from '@/lib/flagged-fields';
import { RENEWAL_MINE_QUERY_KEY } from '@/lib/wizardDataSource';
import { CompanionIdeas } from '@/components/companion/CompanionIdeas';
import { useCompanionIdeas } from '@/components/companion/useCompanionIdeas';
import { CompanionCallout } from '@/components/companion/CompanionCallout';
import { CompanionThread } from '@/components/companion/CompanionThread';
import { companionRequest, type CompanionRequest } from '@/components/companion/companionRequests';
import { CheckinTaskRows } from '@/components/dashboard/CheckinTaskRows';
import { POLLS_FEED_QUERY_KEY, openCheckinTasks, quickPollItems } from '@/components/dashboard/checkinTasks';

// ── Component ───────────────────────────────────────────────────────────────

export function DashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { startGuide, pendingWizardResume, clearWizardResume } = useOnboardingGuide();
  const companionName = useCompanionName();
  const isParent = user?.role === 'parent';

  // #2266: what the thread on this page is asked to send. A prompt handed
  // over from another page (`navigateToStevens`) rides in router state — not
  // the URL, so a refresh does not resend it — and is taken once, here; the
  // state is cleared on arrival. The noticing CTAs append to the same list.
  // The thread acknowledges each request it takes (`onRequestHandled`) and it
  // leaves this list, so a remount of the thread cannot take it a second time.
  // Keyed by the history entry, so a prompt is taken once even if the effect
  // runs twice (StrictMode) or the home was already mounted when it arrived.
  const takenPrefillKeyRef = useRef<string | null>(
    readStevensPrefill(location.state) ? location.key : null,
  );
  const [threadRequests, setThreadRequests] = useState<CompanionRequest[]>(() => {
    const prefill = readStevensPrefill(location.state);
    return prefill ? [companionRequest('send', prefill)] : [];
  });
  const askCompanion = (text: string) =>
    setThreadRequests((current) => [...current, companionRequest('send', text)]);
  const dropThreadRequest = useCallback(
    (id: number) => setThreadRequests((current) => current.filter((r) => r.id !== id)),
    [],
  );
  useEffect(() => {
    const prefill = readStevensPrefill(location.state);
    if (!prefill) return;
    if (takenPrefillKeyRef.current !== location.key) {
      takenPrefillKeyRef.current = location.key;
      setThreadRequests((current) => [...current, companionRequest('send', prefill)]);
    }
    navigate(`${location.pathname}${location.search}`, { replace: true, state: null });
  }, [location.key, location.state, location.pathname, location.search, navigate]);
  // #2109 R3 L1: the page's words are the `dashboard` namespace; the
  // builders return descriptors (`Msg`) that `resolve` reads at render.
  // `grants` loads the one "Year {{n}}" key the renewal sentence names (#2109 R3-B3).
  const { t, i18n } = useTranslation(['dashboard', 'companion', 'grants']);
  const resolve = useMsg();
  const language = i18n.resolvedLanguage ?? i18n.language;

  const { data: tasksData, isLoading: tasksLoading, isError: tasksError, error: tasksErrorObj } = useQuery({
    queryKey: ['tasks'],
    queryFn: () => api.tasks.getAll(),
  });

  // The family's appointments whole — the same payload /medical and /calendar
  // read, one cache entry (#849) — scoped here to the next two days by the
  // noticing engine.
  const { data: appointmentsData } = useQuery({
    queryKey: ['medical', 'appointments'],
    queryFn: () => api.medical.appointments.getAll(),
  });

  const { data: familyData, isLoading: familyLoading, refetch: refetchFamily } = useQuery({
    queryKey: ['family-mine'],
    queryFn: () => api.families.getMine(),
    enabled: isParent,
  });

  const { data: mfaStatusData } = useQuery({
    queryKey: ['mfa-status'],
    queryFn: () => api.mfa.status(),
    enabled: isParent,
  });

  const family = familyData?.data ?? null;
  const mode = resolveDashboardMode(family?.patient?.treatment_phase);
  // One predicate for every treatment-flavoured thing on the page (#685/#693):
  // the records, the copy written about them, the ideas. An in-flight family
  // record counts as memorial because the fail-safe mode is 'active'.
  const memorialGate = familyLoading || mode === 'memorial';

  const [wizardOpen, setWizardOpen] = useState(false);
  const [wizardInitialStep, setWizardInitialStep] = useState<1 | 2 | 3 | 4 | 5>(1);
  const [wizardKey, setWizardKey] = useState(0);
  const wizardShownRef = useRef(false);

  useEffect(() => {
    if (pendingWizardResume !== null) {
      setWizardInitialStep(pendingWizardResume as 1 | 2 | 3 | 4 | 5);
      setWizardKey((k) => k + 1);
      setWizardOpen(true);
      clearWizardResume();
      refetchFamily();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingWizardResume]);

  const isLoading = tasksLoading;
  const isNoFamily = isNoFamilyError(tasksErrorObj);
  // A failed REFETCH keeps the page: the block only stands in when there is
  // nothing to show. Swapping it in over cached data unmounted the thread on
  // the hero, and the conversation in view with it.
  const isError = tasksError && !isNoFamily && !tasksData;

  // The one task filter every parent surface uses (#693), held back while the
  // family record is in flight.
  const rawTasks = tasksData?.data || [];
  const tasks = useMemo(
    () => (familyLoading ? [] : visibleParentTasks(rawTasks, mode)),
    [rawTasks, mode, familyLoading],
  );
  // Appointments are treatment records WHOLE — withheld entirely under the
  // gate (CalendarPage.tsx does the same since #693).
  const appointments = useMemo(
    () => (memorialGate ? [] : appointmentsData?.data || []),
    [appointmentsData, memorialGate],
  );
  const upcomingAppointments = useMemo(
    () => appointments.filter((a) => new Date(a.date_time).getTime() >= Date.now()),
    [appointments],
  );

  const firstName = user?.profile?.first_name ?? '';

  // ── Ideas for you (§2.9 item 2) ──────────────────────────────────────────
  // The generated suggestions and the static chips that stand in until they
  // land — `useCompanionIdeas`, shared with /chat since #2265. The list shows
  // at the START of a conversation only (`CompanionThread` decides).
  const { ideas } = useCompanionIdeas();

  // ── The noticing lanes (§2.9 item 3) ─────────────────────────────────────
  // Polls: an unknown count (loading, failed) asserts nothing — the absence of
  // the sentence is a claim only a resolved fetch may make (AP-58).
  // One key with Community (#2389), so a vote on either page is the count on
  // the other. The feed carries quick polls AND form sends: the polls lane
  // counts quick polls only, and the form sends are item 5's rows.
  const pollsQuery = useQuery({
    queryKey: POLLS_FEED_QUERY_KEY,
    queryFn: () => api.polls.feed(),
    enabled: isParent,
    retry: false,
  });
  const unansweredPolls = pollsQuery.isSuccess
    ? quickPollItems(pollsQuery.data.data ?? []).filter((x) => !x.voted).length
    : null;
  const checkinTasks = useMemo(
    () => (pollsQuery.isSuccess ? openCheckinTasks(pollsQuery.data.data ?? []) : []),
    [pollsQuery.isSuccess, pollsQuery.data],
  );

  // Renewals: due in the final 3 months of the Signature grant, the same
  // rule /bills reads (Victor, 2026-10-03) — so both fetches share /bills'
  // cache keys. The renewal is asked in every month, because a draft under
  // way is offered back whether or not the grant is due. Never asked on a
  // memorial board (the GrantsCard gate, #644/#693).
  const grantsQuery = useQuery({
    queryKey: ['grants-mine'],
    queryFn: () => api.grants.mine(),
    enabled: isParent && !memorialGate,
  });
  // #2973: the receipts waiting on the family — the same fetch and the same
  // count as the /bills status line ("N receipts need you"), shared cache.
  const claimsQuery = useQuery({
    queryKey: CLAIMS_QUERY_KEY,
    queryFn: () => api.reimbursements.listMine(),
  });
  const receiptsNeedYou = claimsQuery.isSuccess
    ? (claimsQuery.data?.data ?? []).filter((c) => needsAttention(c)).length
    : null;
  const renewalDue = grantsQuery.isSuccess
    && signatureRenewalDue(grantsQuery.data?.data ?? []);
  const renewalQuery = useQuery({
    queryKey: RENEWAL_MINE_QUERY_KEY,
    queryFn: () => api.renewals.getMine(),
    enabled: isParent && !memorialGate,
    retry: false,
  });
  const renewalState = renewalQuery.isError
    ? renewalCtaFromError(renewalQuery.error)
    : renewalQuery.isSuccess
      // #2659: with the grants, a decided renewal whose year is over offers the next.
      ? deriveRenewalCta(renewalQuery.data?.data, grantsQuery.isSuccess ? grantsQuery.data?.data ?? [] : null)
      : null;
  // The year in the reader's language (grants:renewal.year), never the English "Year 2".
  const renewalYear = renewalQuery.isSuccess
    ? resolve(renewalYearMsgFromApp(renewalQuery.data?.data)) || null
    : null;
  // #2527: what TRF asked about on a renewal sent back for more info.
  const renewalRecord = renewalQuery.isSuccess ? renewalQuery.data?.data ?? null : null;
  const renewalFlagLabel = useFlaggedFieldLabels(renewalRecord);
  const renewalAsked = renewalState?.kind === 'needs_info' && renewalRecord
    ? askedItemNames(renewalRecord, renewalFlagLabel, resolve, language)
    : null;

  // #2731: the first-visit wizard does not open over a renewal that is due or
  // under way. The renewal invitation is what that family came for, and the
  // wizard's backdrop covers its button. It waits for both renewal fetches to
  // settle, then decides once: a family whose renewal is open is not
  // interrupted this visit, and `shown_at` stays unset for a later one.
  const renewalInPlay = renewalDue
    || renewalState?.kind === 'editable'
    || renewalState?.kind === 'needs_info'
    || renewalState?.kind === 'pending';
  const renewalSettled = (grantsQuery.isSuccess || grantsQuery.isError)
    && (renewalQuery.isSuccess || renewalQuery.isError);
  useEffect(() => {
    if (!family || family.onboarding.shown_at || wizardShownRef.current) return;
    if (!renewalSettled) return;
    wizardShownRef.current = true;
    if (renewalInPlay) return;
    setWizardOpen(true);
  }, [family, renewalSettled, renewalInPlay]);

  const patterns = useMemo(
    () =>
      buildParentPatterns({
        appointments: upcomingAppointments,
        tasks,
        unansweredPolls,
        renewal: { due: renewalDue, state: renewalState, year: renewalYear, asked: renewalAsked },
        grants: grantsQuery.isSuccess ? grantsQuery.data?.data ?? [] : null,
        receiptsNeedYou,
        companionName,
        isMemorial: memorialGate,
        locale: language,
      }),
    [upcomingAppointments, tasks, unansweredPolls, renewalDue, renewalState, renewalYear, renewalAsked, grantsQuery.isSuccess, grantsQuery.data, receiptsNeedYou, companionName, memorialGate, language],
  );
  const calloutPatterns = useMemo<CompanionPattern[]>(
    () =>
      patterns.map((p) => ({
        id: p.id,
        body: resolve(p.body),
        ctaLabel: resolve(p.ctaLabel),
        prompt: resolve(p.prompt),
        href: p.href,
      })),
    [patterns, resolve],
  );
  // #2758 (Mario, 2026-10-12): while the renewal is in play it is the first
  // card on Today — above the setup banner, the composer and the ideas — in
  // the same callout pattern, on its own. The other lanes keep slot 4. A
  // pending renewal says nothing (`buildParentPatterns`), so nothing moves.
  const renewalCallout = renewalInPlay
    ? calloutPatterns.filter((p) => p.id === 'renewal')
    : [];
  const restCallout = renewalCallout.length > 0
    ? calloutPatterns.filter((p) => p.id !== 'renewal')
    : calloutPatterns;
  const noticing = (shown: CompanionPattern[], testId: string) => (
    <CompanionCallout
      patterns={shown}
      label={t('companion:callout.noticed', { companion: companionName })}
      onAsk={askCompanion}
      data-testid={testId}
    />
  );

  // ── Render ───────────────────────────────────────────────────────────────

  const greeting = getDashboardHero(mode, getTimeOfDay()).greeting;
  const memorialCopy = mode === 'memorial' ? memorialHeroCopy(family?.patient?.name) : null;
  const memorial = memorialCopy
    ? {
        eyebrow: resolve(memorialCopy.eyebrow),
        title: memorialCopy.name || resolve(memorialCopy.titleFallback),
        lede: resolve(memorialCopy.lede),
      }
    : null;

  return (
    <>
      {family && wizardOpen && mode !== 'memorial' && (
        <OnboardingWizard
          key={wizardKey}
          family={family}
          initialStep={wizardInitialStep}
          onClose={() => { setWizardOpen(false); refetchFamily(); }}
          onStartGuide={() => { setWizardOpen(false); startGuide(); }}
        />
      )}
      {isLoading ? (
        <div className="flex items-center justify-center py-24">
          <p className="text-stevens-ink-3">{t('states.loading')}</p>
        </div>
      ) : isNoFamily ? (
        <FirstRunCard variant="dashboard" />
      ) : isError ? (
        <div className="flex flex-col items-center justify-center py-24 text-center">
          <AlertCircle className="h-12 w-12 text-destructive mb-4" />
          <p className="text-lg font-medium">{t('states.loadErrorTitle')}</p>
          <p className="text-body text-stevens-ink-3">{t('states.loadErrorBody')}</p>
        </div>
      ) : (
        <div className="max-w-4xl mx-auto space-y-6">
          {/* 1. Header — the greeting alone, no lede (§2.1). Memorial: the
              child's name held plainly; copy is DRAFT PENDING TRF REVIEW, see
              getMemorialHero(). */}
          {memorial ? (
            <PageHead
              density="compact"
              eyebrow={memorial.eyebrow}
              title={memorial.title}
              lede={memorial.lede}
            />
          ) : (
            <PageHead
              density="compact"
              eyebrowKey={NAV_KEY.today}
              title={
                firstName ? (
                  <Trans
                    t={t}
                    i18nKey={GREETING_NAMED_KEY[greeting]}
                    values={{ name: firstName }}
                    components={{ emphasis: <Emphasis>{null}</Emphasis> }}
                  />
                ) : (
                  t(GREETING_ALONE_KEY[greeting])
                )
              }
            />
          )}

          {/* 1a. #2758: an open renewal is what the family came for — it
              leads, above the banner and the composer. */}
          {renewalCallout.length > 0 && noticing(renewalCallout, 'companion-callout-renewal')}

          {/* 2. The one transient block above the composer. */}
          {mode !== 'memorial' && family && !family.onboarding?.banner_dismissed && !family.onboarding?.completed && (
            <OnboardingBanner
              onboarding={family.onboarding}
              // #2385: reopen at the step the banner names, not at step 1.
              onContinue={(step) => {
                setWizardInitialStep(step);
                setWizardKey((k) => k + 1);
                setWizardOpen(true);
              }}
              onDismiss={() => refetchFamily()}
              mfaEnabled={mfaStatusData?.data?.mfa_enabled ?? false}
            />
          )}

          {/* 3. The hero — the thread, the composer, Ideas for you (#2266).
              `stevens-hero` is the anchor `stevens-navigation.ts` targets
              ("take me to the {companion} greeting"). The history drawer is
              the thread's own; it is offered in memorial too (#644). */}
          <div id="stevens-hero" className="flex items-start gap-2">
            <div className="flex-1 min-w-0">
              <CompanionThread
                variant="home"
                lazy
                requests={threadRequests}
                onRequestHandled={dropThreadRequest}
                ariaLabel={t('composer.ariaLabel', { companion: companionName })}
                placeholder={t('composer.placeholder', { companion: companionName })}
                // Bounded, with its own scroll: the greeting stays on screen
                // above a long thread, and the composer below it.
                threadClassName="max-h-[50dvh]"
                renderIdeas={
                  memorialGate
                    ? undefined
                    : ({ hidden, disabled, send }) => (
                        <CompanionIdeas
                          ideas={ideas}
                          hidden={hidden}
                          disabled={disabled}
                          onPick={(idea) => send(idea.message)}
                        />
                      )
                }
              />
            </div>
          </div>

          {/* 4. {companion} · noticed something — the last element of the hero. */}
          {restCallout.length > 0 && noticing(restCallout, 'companion-callout')}

          {/* 5. For you to answer — check-ins and form sends, as to-do rows
              beside the noticing's to-dos (2026-10-23). Nothing when nothing is owed. */}
          {isParent && <CheckinTaskRows items={checkinTasks} />}
        </div>
      )}
    </>
  );
}
